import React, { useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { Sync as SyncIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { apiService, ContestActivitySyncError } from '../../services/api.service';
import { SnackbarState } from './useContestActions';

interface ContestActivitySyncButtonProps {
  contestId: string;
  onSynced: () => void;
  notify: (message: string, severity: SnackbarState['severity']) => void;
}

// Lets a participant who joined late pull in their activities since the contest start.
export const ContestActivitySyncButton = ({ contestId, onSynced, notify }: ContestActivitySyncButtonProps) => {
  const { t } = useTranslation('contests');
  const [syncing, setSyncing] = useState(false);

  const sync = async () => {
    setSyncing(true);
    try {
      const { creditedCount, strava } = await apiService.syncContestActivities(contestId);
      if (creditedCount > 0) onSynced();
      if (strava?.stravaBusy) {
        // Stored workouts were still credited; only the fresh Strava import was skipped.
        notify(t('activitySync.stravaBusy', { count: creditedCount }), 'warning');
      } else if (creditedCount > 0) {
        notify(t('activitySync.credited', { count: creditedCount }), 'success');
      } else {
        notify(t(strava ? 'activitySync.nothingNew' : 'activitySync.nothingNewNoStrava'), 'info');
      }
    } catch (error) {
      if (error instanceof ContestActivitySyncError && error.status === 409) {
        notify(t('activitySync.inProgress'), 'info');
      } else if (error instanceof ContestActivitySyncError && error.status === 429) {
        notify(t('activitySync.tooMany'), 'warning');
      } else {
        notify(error instanceof Error ? error.message : t('activitySync.failed'), 'error');
      }
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Button variant="outlined" startIcon={syncing ? <CircularProgress size={14} color="inherit" /> : <SyncIcon />} onClick={sync} disabled={syncing}>
      {t('activitySync.button')}
    </Button>
  );
};
